import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import { Gallery } from "@organisms/gallery";
import { LayoutTemplate } from "@templates/layout";
import React, { FC, Fragment, useCallback, useState } from "react";
import styled from "styled-components";
import Button from "@atoms/button";
import {
  BookingHandler,
  CalculatePriceHandler,
  DialogBooking,
} from "@organisms/dialog-booking";
import { Price } from "@atoms/price";
import { SearchForm } from "@organisms/searcher";
import { GetCalendarHandler } from "@molecules/booking-form-fields/interfaces";
import { NoSSR } from "@atoms/nossr/nossr";
import { ContactSection } from "@organisms/contact-section";
import { ContactForm } from "@molecules/contact-form";
import { ContactFormData } from "@molecules/contact-form/types";

export type HomeSheetTemplateProps = {
  onBookingRequest: BookingHandler;
  onCalculatePrice: CalculatePriceHandler;
  onGetCalendar: GetCalendarHandler;
  onPropertyContact: (form: ContactFormData) => Promise<boolean>;

  title: string;
  minPrice: number | null;
  description?: string;
  propertyType?: string;
  people?: number;
  doors?: number;
  beds?: number;
  amenities?: { code: string; name: string }[];
  photos?: { title: string; resource: string }[];
  searchFormData?: SearchForm;
};

export const HomeSheetTemplate: FC<HomeSheetTemplateProps> = ({
  onBookingRequest,
  onCalculatePrice,
  onGetCalendar,
  onPropertyContact,

  title,
  minPrice,
  description,
  propertyType,
  people,
  doors,
  beds,
  amenities,
  photos,
  searchFormData,
}) => {
  const [openBookingForm, setOpenBookingForm] = useState(false);

  const handleOpenBookingForm = useCallback(() => setOpenBookingForm(true), []);
  const handleCloseBookingForm = useCallback(
    () => setOpenBookingForm(false),
    []
  );

  return (
    <LayoutTemplate page="reservations">
      <Container>
        <div className="content">
          <Typography variant="title1">{title}</Typography>
          <div className="property-features">
            {propertyType && (
              <div>
                <Icon name="home-2" /> {propertyType}
              </div>
            )}
            {people && (
              <div>
                <Icon name="people" /> {people} people
              </div>
            )}
            {doors && (
              <div>
                <Icon name="door" /> {doors} rooms
              </div>
            )}
            {beds && (
              <div>
                <Icon name="bed" /> {beds} beds
              </div>
            )}
          </div>
        </div>
        {photos && (
          <div className="content">
            <Gallery photos={photos} />
          </div>
        )}

        {description && (
          <div className="content-highlight">
            <div className="content">
              <Typography variant="title2">Description</Typography>
              <Typography variant="body1" as="div">
                <div dangerouslySetInnerHTML={{ __html: description }} />
              </Typography>
            </div>
          </div>
        )}
        {amenities && amenities.length > 0 && (
          <div className="content">
            <Typography variant="title2">Amenities</Typography>
            <div className="property-amenities">
              {amenities.map((c) => (
                <div key={`amenity_${c.code}`}>
                  <Icon name={c.code as any} /> {c.name}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="content-highlight">
          <div className="content">
            <Typography variant="title2">Contact with us</Typography>
            <ContactSection
              className="contact-form"
              form={ContactForm}
              onSubmit={onPropertyContact}
            />
          </div>
        </div>

        {minPrice && (
          <div className="home-actions">
            <Button variant="action" onClick={handleOpenBookingForm}>
              Book from <Price amount={minPrice} asInteger />
            </Button>
          </div>
        )}
      </Container>
      <NoSSR>
        <DialogBooking
          open={openBookingForm}
          onClose={handleCloseBookingForm}
          searchFormData={searchFormData}
          onCalculatePrice={onCalculatePrice}
          onBooking={onBookingRequest}
          onGetCalendar={onGetCalendar}
        />
      </NoSSR>
    </LayoutTemplate>
  );
};

const Container = styled.div`
  position: relative;

  .home-actions {
    position: fixed;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    bottom: 1em;
    width: 100%;
    button {
      font-size: 1.2em;
      border-radius: 0.5em;
      box-shadow: 0px 10px 13px -7px #000000, 5px 5px 15px 5px rgba(0, 0, 0, 0) !important;
      :hover {
        transform: Scale(1.1);
      }
    }
  }

  .content {
    max-width: 75em;
    margin: 0 auto;
    padding: 0 1em;
  }

  .content-highlight {
    background-color: #f9f9f9;
    padding: 1em 0;
    box-sizing: border-box;
    margin: 1em 0;
  }

  .content {
    max-width: 75em;
    margin: 0 auto;
  }

  h1 {
    margin-bottom: 0.5em;
  }
  .property-features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(5em, 1fr));
    justify-content: stretch;
    gap: 0.2em 2em;
    margin-bottom: 2em;
    max-width: 30em;
    > div {
      color: #1d1d1d;
      display: flex;
      gap: 0.2em;
      align-items: center;
      font-size: 1em;
      font-family: "Work Sans", Arial;
      white-space: nowrap;
      .fh-icon {
        color: #fdbb30;
        font-size: 1.5em;
      }
    }
  }

  .property-amenities {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15em, 1fr));
    justify-content: stretch;
    gap: 2em;
    margin-bottom: 1.5em;
    font-weight: 300;
    > div {
      color: #1d1d1d;
      display: flex;
      gap: 0.2em;
      align-items: center;
      font-size: 1.2em;
      font-family: "Work Sans", Arial;
      letter-spacing: 0.5px;
      white-space: nowrap;
      .fh-icon {
        color: #1d1d1d;
        font-size: 1.5em;
      }
    }
  }

  @media (min-width: 48em) {
    .content {
      padding: 0 2em;
    }
  }
`;
